<template>
    <Calendar
        class="order-date-picker"
        :class="calendarClass"
        panelClass="order-date-picker-panel"
        v-model="dateValue"
        :manualInput="false"
        :dateFormat="dateFormat"
        :minDate="currentMinDate"
        :maxDate="currentMaxDate"
        :disabledDates="props.disabledDates"
        :inputId="props.inputId"
        showIcon
        showButtonBar
        iconDisplay="input"
    >
        <template #inputicon>
            <i class="calendar-dropdown-icon far fa-chevron-down"></i>
        </template>
        <template #date="slotProps">
            <strong v-if="isDisabledDate(slotProps.date)" style="text-decoration: line-through">{{
                slotProps.date.day
            }}</strong>
            <template v-else>{{ slotProps.date.day }}</template>
        </template>
    </Calendar>
</template>

<script setup lang="ts">
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref, watch } from "vue";
    import Calendar from 'primevue/calendar';

    // define props
    const props = defineProps<{
        calendarClass?: string
        modelValue: Date | null | undefined;
        minDate?: Date;
        maxDate?: Date;
        disabledDates?: Date[];
        inputId?: string;
    }>();

    // define emits
    const emit = defineEmits(["update:modelValue"]);

    // define state
    const dateValue = ref(props.modelValue);
    const dateFormat = computed(() => {
        return dateValue.value != null && DateTime.isToday(dateValue.value)
            ? "D '- " + translations.general.today.replace("'", "''") + "' dd.mm.yy"
            : "D '-' dd.mm.yy";
    });
    const currentMinDate = computed(() => {
        return props.minDate ?? new Date();
    });
    const currentMaxDate = computed(() => {
        return props.maxDate;
    });
    const disabledDateDict = computed(() => {
        const result = {};
        props.disabledDates?.forEach((date) => {
            result[DateTime.toDateString(date)] = true;
        });
        return result;
    });

    // define functions
    function isDisabledDate(date: { day: number; month: number; year: number; today: boolean; selectable: boolean }) {
        const dateString = DateTime.toDateString(new Date(date.year, date.month, date.day));
        const isDisabledDate = disabledDateDict.value[dateString];
        return isDisabledDate == true;
    }

    // event handlers
    watch(dateValue, (newValue) => {
        emit("update:modelValue", newValue);
    });

    // update watchers
    watch(
        () => props.modelValue,
        (newValue) => {
            dateValue.value = newValue;
        }
    );
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .order-date-picker {
        input {
            text-align: left;
            color: colors.$fontColor;
            min-width: 100px;
            width: 300px;
            border-radius: 4px;
            padding: 0.75em;
            font-size: 18px !important;
            padding-right: 2em;
            box-shadow: none !important;
            cursor: pointer;
        }

        .calendar-dropdown-icon {
            right: 1rem !important;
            cursor: pointer;

            font-family: "Font Awesome 5 Pro";
        }
    }

    // kiosk: the field is sized in px, so its width, its text and the corner radius scale
    // together. the paddings are in em and the chevron inherits the font size, so both follow
    .kiosk-mode .order-date-picker {
        input {
            min-width: 100px * variables.$kioskScale;
            width: 300px * variables.$kioskScale;
            border-radius: 4px * variables.$kioskScale;
            font-size: 18px * variables.$kioskScale !important;
        }

        // rem is root-relative, so this inset is the one thing that does not follow the field
        .calendar-dropdown-icon {
            right: 1rem * variables.$kioskScale !important;
        }
    }

    // the panel hangs off the body and the primevue theme sizes it in rem, so nothing in
    // here follows the field either. every value is the theme default at the kiosk scale,
    // and the extra .p-datepicker keeps each rule ahead of the theme rule it replaces
    .kiosk-mode .order-date-picker-panel.p-datepicker {
        padding: 0.5rem * variables.$kioskScale;

        .p-datepicker-header {
            padding: 0.5rem * variables.$kioskScale;

            .p-datepicker-prev,
            .p-datepicker-next {
                width: 2rem * variables.$kioskScale;
                height: 2rem * variables.$kioskScale;

                .p-icon {
                    width: 1rem * variables.$kioskScale;
                    height: 1rem * variables.$kioskScale;
                }
            }

            .p-datepicker-title {
                line-height: 2rem * variables.$kioskScale;
                font-size: 1rem * variables.$kioskScale;

                .p-datepicker-year,
                .p-datepicker-month {
                    // .p-link pins these to the theme font size, so they do not inherit
                    font-size: 1rem * variables.$kioskScale;
                    padding: 0.5rem * variables.$kioskScale;
                }

                .p-datepicker-month {
                    margin-right: 0.5rem * variables.$kioskScale;
                }
            }
        }

        table {
            font-size: 1rem * variables.$kioskScale;
            margin: (0.5rem * variables.$kioskScale) 0;

            th,
            td {
                padding: 0.5rem * variables.$kioskScale;

                > span {
                    width: 2.5rem * variables.$kioskScale;
                    height: 2.5rem * variables.$kioskScale;
                }
            }
        }

        .p-datepicker-buttonbar {
            padding: (1rem * variables.$kioskScale) 0;

            .p-button {
                padding: (0.5rem * variables.$kioskScale) (1rem * variables.$kioskScale);
                font-size: 1rem * variables.$kioskScale;
            }
        }
    }
</style>
